import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import "../css/css_pages/Dashboard.css";
import { FaCheck, FaSync, FaSearch, FaTicketAlt } from "react-icons/fa";
import Cookies from "js-cookie";
import { FiImage, FiEdit } from "react-icons/fi";
import config from "../../config";
import PopupMessage from "../layouts/PopupMessage";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import io from "socket.io-client";
import TicketPopup from "./TicketPopup";
import moment from "moment";

const Dashboard = () => {
  const [token, setToken] = useState(null);
  const [assignedTSOs, setAssignedTSOs] = useState({});
  const [filteredIssues, setFilteredIssues] = useState([]);
  const [allIssues, setAllIssues] = useState([]);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [statuses, setStatuses] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [alertMessage, setAlertMessage] = useState(null);
  const [selectedFile, setSelectedFile] = useState({ name: "", type: "" });
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [reporterFilter, setReporterFilter] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [tsoFilter, setTsoFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [productFilter, setProductFilter] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [dateFilterMode, setDateFilterMode] = useState("single");
  const [activeQuickFilter, setActiveQuickFilter] = useState("today");
  const [showTicketPopup, setShowTicketPopup] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [ticketToUpdate, setTicketToUpdate] = useState(null);
  const [contactPhone, setContactPhone] = useState("");
  const [filesToUpload, setFilesToUpload] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [socketConnected, setSocketConnected] = useState(false);
  const [itemsPerPage] = useState(100); // Display 100 items per page
  const [currentPage, setCurrentPage] = useState(1);
  const socketRef = useRef(null);
  const [filterOptions, setFilterOptions] = useState({
    reporters: [],
    clients: [],
    types: [],
    priorities: [],
    products: [],
    tsos: [],
  });

  const navigate = useNavigate();
  const previousIssuesRef = useRef([]);
  const issueTypes = ["Not Working", "Data Corrections"];
  const statusSelectRef = useRef(null);
  const reporterSelectRef = useRef(null);
  const clientSelectRef = useRef(null);
  const typeSelectRef = useRef(null);
  const prioritySelectRef = useRef(null);
  const productSelectRef = useRef(null);
  const tsoSelectRef = useRef(null);

  const uniqueFilteredIssues = useMemo(() => {
    const seen = new Set();
    return filteredIssues.filter((issue) => {
      if (seen.has(issue.ticket_id)) return false;
      seen.add(issue.ticket_id);
      return true;
    });
  }, [filteredIssues]);

  // Memoize sorted issues to avoid re-sorting on every render
  const sortedIssues = useMemo(() => {
    return [...uniqueFilteredIssues].sort(
      (a, b) =>
        new Date(b.ticket_created_date) - new Date(a.ticket_created_date)
    );
  }, [uniqueFilteredIssues]);

  // Paginate sorted issues
  const paginatedIssues = useMemo(() => {
    const startIdx = (currentPage - 1) * itemsPerPage;
    const endIdx = startIdx + itemsPerPage;
    return sortedIssues.slice(startIdx, endIdx);
  }, [sortedIssues, currentPage, itemsPerPage]);

  const totalPages = useMemo(() => {
    return Math.ceil(sortedIssues.length / itemsPerPage);
  }, [sortedIssues.length, itemsPerPage]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [uniqueFilteredIssues]);

  const formatDateTime = (dateString, showOnlyTime = false) => {
    if (!dateString) return "-";
    const date = moment(dateString);
    if (!date.isValid()) {
      console.warn(`Invalid date string: ${dateString}`);
      return "Invalid Date";
    }

    if (showOnlyTime) {
      return date.format("hh:mm A");
    }
    return date.format("MM/DD/YYYY, hh:mm A");
  };

  const applyFilters = useCallback(
    (issues) => {
      let filtered = Array.isArray(issues) ? [...issues] : [issues];

      // Note: Status filtering is handled by the backend API, so we don't filter by selectedStatus here
      // This prevents double-filtering and ensures consistency

      // Helper function to get date in local timezone without time conversion
      const getLocalDateString = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      };

      // Apply quick filter date ranges on the client side
      if (activeQuickFilter === "today") {
        const today = new Date().toISOString().split("T")[0];
        filtered = filtered.filter((i) => {
          const ticketDate = getLocalDateString(i.ticket_created_date);
          return ticketDate === today;
        });
      } else if (activeQuickFilter === "all") {
        // "All" should show all fetched data (already fetched from backend for full date range)
        // No additional filtering needed
      } else if (dateFilterMode === "single" && selectedDate) {
        const selectedDateStr = selectedDate.toISOString().split("T")[0];
        filtered = filtered.filter((i) => {
          const ticketDate = getLocalDateString(i.ticket_created_date);
          return ticketDate === selectedDateStr;
        });
      } else if (dateFilterMode === "range" && startDate && endDate) {
        const startStr = startDate.toISOString().split("T")[0];
        const endStr = endDate.toISOString().split("T")[0];
        filtered = filtered.filter((i) => {
          const ticketDate = getLocalDateString(i.ticket_created_date);
          return ticketDate >= startStr && ticketDate <= endStr;
        });
      }

      if (searchTerm) {
        filtered = filtered.filter(
          (i) =>
            i.issue && i.issue.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      if (reporterFilter) {
        filtered = filtered.filter((i) => i.created_by === reporterFilter);
      }

      if (clientFilter) {
        filtered = filtered.filter((i) => i.customer_name === clientFilter);
      }

      if (typeFilter) {
        filtered = filtered.filter((i) => i.ticket_type === typeFilter);
      }

      if (priorityFilter) {
        filtered = filtered.filter((i) => i.ticket_priority === priorityFilter);
      }

      if (productFilter) {
        filtered = filtered.filter((i) => i.product === productFilter);
      }

      // For "My Tasks", filter by current user's ID as TSO
      if (activeQuickFilter === "myTasks" && currentUser?.id) {
        filtered = filtered.filter((i) => i.assigned_tso_id === currentUser.id);
      } else if (tsoFilter) {
        filtered = filtered.filter((i) => i.assigned_tso_name === tsoFilter);
      }

      return filtered;
    },
    [
      selectedDate,
      startDate,
      endDate,
      dateFilterMode,
      searchTerm,
      reporterFilter,
      clientFilter,
      typeFilter,
      priorityFilter,
      productFilter,
      tsoFilter,
      activeQuickFilter,
      currentUser?.id,
    ]
  );

  useEffect(() => {
    const authToken = Cookies.get("authToken");
    setToken(authToken);
  }, []);

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchCurrentUser();
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchTicketStatuses();
    }
  }, [token]);

  useEffect(() => {
    if (allIssues.length > 0) {
      const filtered = applyFilters(allIssues);
      setFilteredIssues(filtered);

      // Extract unique dynamic filter options from allIssues only once per data load
      const reporters = Array.from(
        new Set(
          allIssues
            .map((i) => i.created_by)
            .filter((name) => name && name.trim())
        )
      );

      const types = Array.from(
        new Set(
          allIssues
            .map((i) => i.ticket_type)
            .filter((type) => type && type.trim())
        )
      );

      const priorities = Array.from(
        new Set(
          allIssues
            .map((i) => i.ticket_priority)
            .filter((priority) => priority && priority.trim())
        )
      );

      const products = Array.from(
        new Set(
          allIssues
            .map((i) => i.product)
            .filter((product) => product && product.trim())
        )
      );

      setFilterOptions((prev) => ({
        ...prev,
        reporters,
        types,
        priorities,
        products,
      }));
    }
  }, [allIssues, applyFilters]);

  useEffect(() => {
    const initializeSocket = () => {
      const baseUrl = config.API_URL.replace("/api", "");
      const authToken = Cookies.get("authToken");

      const newSocket = io(baseUrl, {
        withCredentials: true,
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        transports: ["websocket", "polling"], // Try websocket first, then fallback to polling
        auth: {
          token: authToken,
        },
        query: {
          token: authToken,
        },
        extraHeaders: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      newSocket.on("connect", () => {
        console.log("✅ Socket connected successfully!");
        console.log("  - Socket ID:", newSocket.id);
        console.log("  - Transport:", newSocket.io.engine.transport.name);
        setSocketConnected(true);
      });

      newSocket.on("disconnect", () => {
        console.log("❌ Socket disconnected");
        setSocketConnected(false);
      });

      newSocket.on("connect_error", (err) => {
        console.error("❌ Socket connection error:");
        console.error("  - Message:", err.message);
        console.error("  - Code:", err.code);
        console.error("  - Full error:", err);
        setSocketConnected(false);
      });

      // Simple ticket refresh trigger
      newSocket.on("ticket_refresh", async (data) => {
        console.log("🔄 Received ticket_refresh signal:", data);

        try {
          // Show notification first
          showPopupMessage("Ticket has been updated. Refreshing data...");

          // Trigger a fresh data fetch
          await refetchCurrentData();

          console.log("✅ Data refreshed successfully after ticket update");
        } catch (error) {
          console.error("❌ Error refreshing data:", error);
          showPopupMessage("Failed to refresh ticket data");
        }
      });

      // Optional: Handle specific ticket operations
      newSocket.on("ticket_added", (data) => {
        console.log("🎟️ Received ticket_added signal:", data);
        showPopupMessage("New Ticket Received");
        refetchCurrentData();
      });

      // Handle ticket completion notifications
      newSocket.on("ticket_completed", (data) => {
        console.log("✅ Received ticket_completed signal:", data);
        showPopupMessage("Ticket completed successfully. Refreshing...");
        refetchCurrentData();
      });

      socketRef.current = newSocket;
      return newSocket;
    };

    let socket = null;

    if (token && currentUser?.id) {
      socket = initializeSocket();
    }

    return () => {
      if (socket) {
        socket.off("ticket_refresh");
        socket.off("ticket_added");
        socket.off("ticket_completed");
        socket.off("ticket_updated");
        socket.off("ticket_status_changed");
        socket.off("connect");
        socket.off("disconnect");
        socket.off("connect_error");
        socket.disconnect();
        console.log("🧹 Socket cleaned up");
      }
    };
  }, [token, currentUser?.id]);

  const refetchCurrentData = async () => {
    try {
      setIsRefreshing(true);

      // Determine what data to fetch based on current filters
      if (selectedStatus) {
        // If we have a status filter active, refetch by status
        await fetchIssuesByStatus(selectedStatus);
      } else if (selectedDate) {
        // If we have a date filter, refetch for that date
        const dateStr = selectedDate.toISOString().split("T")[0];
        await fetchIssues(dateStr, dateStr);
      } else {
        // Default: refetch today's data (or whatever the default view is)
        const currentDate = new Date().toISOString().split("T")[0];
        await fetchIssues(currentDate, currentDate);
      }

      // Update last refreshed time
      setLastUpdated(new Date());
    } catch (error) {
      console.error("Error refetching data:", error);
      throw error; // Re-throw so calling code can handle it
    } finally {
      setIsRefreshing(false);
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!selectedIssue.issueType) {
      errors.issueType = "Issue Type is required";
    }

    if (!selectedIssue.comment || selectedIssue.comment.length < 3) {
      errors.comment = "Comment must be at least 3 characters";
    }

    if (!selectedIssue.successfullyCompleted) {
      errors.successfullyCompleted = "Please confirm successful completion";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const fetchData = async () => {
    try {
      setIsRefreshing(true);

      // Fetch ticket statuses, current user, clients, and TSOs in parallel
      await Promise.all([
        fetchTicketStatuses(),
        fetchCurrentUser(),
        fetchAllClients(),
        fetchAllTSOs(),
      ]);

      // Fetch today's issues (initial default view)
      const today = new Date().toISOString().split("T")[0];
      const issues = await fetchIssues(today, today);

      const uniqueCustomerIds = [
        ...new Set(issues.map((issue) => issue.customer_id)),
      ];

      await Promise.all(
        uniqueCustomerIds.map((customerId) => fetchAssignedTSO(customerId))
      );

      setLastUpdated(new Date());
    } catch (error) {
      console.error("Error fetching data:", error);
      setError(error.message);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    try {
      await refetchCurrentData();
      showPopupMessage("Data refreshed successfully");
    } catch (error) {
      console.error("Error during manual refresh:", error);
      showPopupMessage("Error refreshing data");
    }
  };

  const showPopupMessage = (message) => {
    setAlertMessage(message);
  };

  const closePopupMessage = () => {
    setAlertMessage(null);
  };

  const fetchAssignedTSO = async (customerId) => {
    try {
      setLoading(true);
      setError(null);
      const token = Cookies.get("authToken");
      if (!token) {
        throw new Error("Authentication token not found.");
      }

      const response = await fetch(
        `${config.API_URL}/tso-online/get-tso-by-customer-id?customer_id=${customerId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch TSO list");
      }

      const tso = await response.json();
      const tsoArray = Array.isArray(tso) ? tso : [tso];

      setAssignedTSOs((prev) => ({
        ...prev,
        [customerId]: tsoArray,
      }));

      return tsoArray;
    } catch (error) {
      console.error("Error fetching TSO list:", error);
      setError(error.message);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Status color cache to prevent object recreation
  const STATUS_COLOR_MAP = useMemo(
    () => ({
      pending: { backgroundColor: "#faafaf", color: "#2f0909" },
      in_progress: { backgroundColor: "#fff3cd", color: "#856404" },
      completed: { backgroundColor: "#d4edda", color: "#155724" },
      closed: { backgroundColor: "#d6d8d9", color: "#1b1e21" },
      default: { backgroundColor: "#f8f9fa", color: "#212529" },
    }),
    []
  );

  const getStatusColor = useCallback(
    (status) => {
      return STATUS_COLOR_MAP[status] || STATUS_COLOR_MAP.default;
    },
    [STATUS_COLOR_MAP]
  );

  // Memoize status text transformation
  const formatStatusText = useCallback((status) => {
    return status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  }, []);

  // Memoize filename parsing
  const getFileList = useCallback((filename) => {
    if (!filename) return [];
    return filename.split(",");
  }, []);

  const openResource = async (issue, resource) => {
    setSelectedIssue(issue);
    const url = `${config.API_URL}/tso-online/view-image?ticket_id=${issue.ticket_id}&file_name=${resource}`;
    const token = Cookies.get("authToken");
    if (!token) {
      throw new Error("Authentication token not found.");
    }

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const fileType = blob.type;
        setSelectedFile({ name: resource, type: fileType });
        const imageURL = URL.createObjectURL(blob);
        setImageSrc(imageURL);
      } else {
        console.error("Failed to fetch the resource:", response.statusText);
        showPopupMessage("Failed to fetch resource");
      }
    } catch (error) {
      console.error("Error fetching the resource:", error);
      showPopupMessage("Error fetching resource");
    }
  };

  const closeImageModal = () => {
    setImageSrc(null);
    setSelectedFile({ name: "", type: "" });
  };

  const fetchCurrentUser = async () => {
    try {
      const token = Cookies.get("authToken");
      if (!token) {
        throw new Error("Authentication token not found.");
      }

      const response = await fetch(`${config.API_URL}/user/profile`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch current user.");
      }
      const user = await response.json();
      setCurrentUser(user);
    } catch (error) {
      console.error("Error fetching current user:", error);
      setError(error.message);
    }
  };

  const fetchIssues = async (startDate, endDate) => {
    try {
      const token = Cookies.get("authToken");
      if (!token) {
        throw new Error("Authentication token not found.");
      }

      const response = await fetch(
        `${config.API_URL}/tso-online/get-all-tickets?start_date=${startDate}&end_date=${endDate}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch issues: ${response.statusText}`);
      }
      const data = await response.json();
      previousIssuesRef.current = filteredIssues;
      setAllIssues(data);
      setFilteredIssues(applyFilters(data));
      console.log("Fetched issues:", data.length);
      return data;
    } catch (error) {
      console.error("Error fetching issues:", error);
      setError(error.message);
      return [];
    }
  };

  const fetchIssuesByStatus = async (ticket_status) => {
    try {
      if (!ticket_status) {
        // If status is being cleared, fetch ALL tickets and re-apply all filters
        setSelectedStatus(null);

        // Use the same date range as "All" button to fetch all tickets
        const startDate = "2000-01-01";
        const currentDate = new Date().toISOString().split("T")[0];
        const endDate = currentDate;

        // Fetch all tickets for the full date range
        const token = Cookies.get("authToken");
        if (!token) {
          throw new Error("Authentication token not found.");
        }

        const response = await fetch(
          `${config.API_URL}/tso-online/get-all-tickets?start_date=${startDate}&end_date=${endDate}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch tickets: ${response.statusText}`);
        }

        const data = await response.json();
        setAllIssues(data);
        // Don't set filteredIssues here - let the useEffect handle it with applyFilters
        console.log("Cleared status filter, fetched all tickets:", data.length);
        return;
      }

      // Status is being set to a specific value - reset other filters
      setSearchTerm("");
      setSelectedDate(null);
      setReporterFilter("");
      setClientFilter("");
      setTypeFilter("");
      setPriorityFilter("");
      setProductFilter("");
      setTsoFilter("");

      // Clear all other filter refs
      if (reporterSelectRef.current) reporterSelectRef.current.clearValue();
      if (clientSelectRef.current) clientSelectRef.current.clearValue();
      if (typeSelectRef.current) typeSelectRef.current.clearValue();
      if (prioritySelectRef.current) prioritySelectRef.current.clearValue();
      if (productSelectRef.current) productSelectRef.current.clearValue();
      if (tsoSelectRef.current) tsoSelectRef.current.clearValue();

      setSelectedStatus(ticket_status);

      const token = Cookies.get("authToken");
      if (!token) {
        throw new Error("Authentication token not found.");
      }

      const response = await fetch(
        `${config.API_URL}/tso-online/get-tickets-by-status?status=${ticket_status}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch issues by status: ${response.statusText}`
        );
      }
      const data = await response.json();
      setAllIssues(data);
      setFilteredIssues(data); // Don't apply filters since API already filtered by status
      console.log(`Fetched issues for status ${ticket_status}:`, data.length);
    } catch (error) {
      console.error("Error fetching issues by status:", error);
      setError(error.message);
    }
  };

  const fetchTicketStatuses = async () => {
    try {
      const token = Cookies.get("authToken");
      if (!token) {
        throw new Error("Authentication token not found.");
      }

      const response = await fetch(
        `${config.API_URL}/tso-online/get-ticket-statuses`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch ticket statuses: ${response.statusText}`
        );
      }
      const data = await response.json();
      setStatuses(data);
      console.log("Fetched ticket statuses:", data);
    } catch (error) {
      console.error("Error fetching ticket statuses:", error);
      setError(error.message);
    }
  };

  const fetchAllClients = async () => {
    try {
      const token = Cookies.get("authToken");
      if (!token) {
        throw new Error("Authentication token not found.");
      }

      const response = await fetch(
        `${config.API_URL}/tso-online/get-all-clients`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch clients: ${response.statusText}`);
      }

      const data = await response.json();
      const clientNames = data
        .map((client) => client.customer_name)
        .filter((name, index, self) => name && self.indexOf(name) === index);

      setFilterOptions((prev) => ({
        ...prev,
        clients: clientNames,
      }));

      console.log("Fetched client names:", clientNames);
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  const fetchAllTSOs = async () => {
    try {
      const token = Cookies.get("authToken");
      if (!token) {
        throw new Error("Authentication token not found.");
      }

      const response = await fetch(`${config.API_URL}/tso-online/get-all-tso`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch TSOs: ${response.statusText}`);
      }

      const data = await response.json();

      // Handle both array response and wrapped response
      const tsoList = Array.isArray(data) ? data : data?.response || [];

      // Extract TSO names, handling both tso_name and calling_name/full_name fields
      const tsoNames = tsoList
        .map((tso) => tso.tso_name || tso.calling_name || tso.full_name)
        .filter(
          (name, index, self) =>
            name && name.trim() && self.indexOf(name) === index
        );

      setFilterOptions((prev) => ({
        ...prev,
        tsos: tsoNames,
      }));

      console.log("Fetched TSO names:", tsoNames);
      console.log("Raw TSO data:", tsoList);
    } catch (error) {
      console.error("Error fetching TSOs:", error);
    }
  };

  const handleAssignToMe = async (
    tso_id,
    ticket_id,
    customer_id,
    issue,
    created_date,
    comment
  ) => {
    try {
      const token = Cookies.get("authToken");
      if (!token) {
        throw new Error("Authentication token not found.");
      }

      const response = await fetch(
        `${config.API_URL}/tso-online/assign-ticket-to-tso`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            tso_id: tso_id,
            ticket_id: ticket_id,
            customer_id: customer_id,
            issue: issue + " | RESOLVED- " + comment,
            scheduled_date: created_date,
            complete: true,
          }),
        }
      );

      if (response.status === 200) {
        showPopupMessage("Ticket is completed by you successfully.");
        // Emit socket event to notify all connected clients about ticket completion
        if (socketRef.current) {
          socketRef.current.emit("ticket_completed", {
            ticket_id: ticket_id,
            tso_id: tso_id,
            timestamp: new Date(),
          });
        }
        // Refresh data after slight delay to allow backend to process
        setTimeout(() => {
          refetchCurrentData();
        }, 500);
      } else {
        showPopupMessage("Error assigning ticket.");
      }
    } catch (error) {
      console.error("Error updating ticket status:", error);
      showPopupMessage(`Error assigning ticket: ${error.message}`);
    }
  };

  const filterIssues = (filterType) => {
    const currentDate = new Date();
    let startDate, endDate;

    switch (filterType) {
      case "today":
        startDate = endDate = currentDate.toISOString().split("T")[0];
        break;
      case "all":
        startDate = "2000-01-01";
        endDate = currentDate.toISOString().split("T")[0];
        break;
      case "myTasks":
        startDate = "2000-01-01";
        endDate = currentDate.toISOString().split("T")[0];
        break;
      default:
        startDate = endDate = currentDate.toISOString().split("T")[0];
    }

    // Set the active quick filter
    setActiveQuickFilter(filterType);

    // Reset all filters when clicking a quick filter button
    setSelectedStatus(null);
    setSearchTerm("");
    setSelectedDate(null);
    setStartDate(null);
    setEndDate(null);
    setDateFilterMode("single");
    setReporterFilter("");
    setClientFilter("");
    setTypeFilter("");
    setPriorityFilter("");
    setProductFilter("");

    // For "My Tasks", filter by current user as TSO
    if (filterType === "myTasks") {
      setTsoFilter(currentUser?.calling_name || "");
    } else {
      setTsoFilter("");
    }

    // Reset Select component refs to clear their values visually
    if (statusSelectRef.current) statusSelectRef.current.clearValue();
    if (reporterSelectRef.current) reporterSelectRef.current.clearValue();
    if (clientSelectRef.current) clientSelectRef.current.clearValue();
    if (typeSelectRef.current) typeSelectRef.current.clearValue();
    if (prioritySelectRef.current) prioritySelectRef.current.clearValue();
    if (productSelectRef.current) productSelectRef.current.clearValue();
    if (tsoSelectRef.current) tsoSelectRef.current.clearValue();

    // Fetch issues with the specified date range (only table updates, no page reload)
    fetchIssues(startDate, endDate);
  };

  const clearAllFilters = async () => {
    try {
      // Reset all filter states
      setSelectedStatus(null);
      setSearchTerm("");
      setSelectedDate(null);
      setStartDate(null);
      setEndDate(null);
      setDateFilterMode("single");
      setReporterFilter("");
      setClientFilter("");
      setTypeFilter("");
      setPriorityFilter("");
      setProductFilter("");
      setTsoFilter("");

      // Reset Select component refs to clear their values visually
      if (statusSelectRef.current) statusSelectRef.current.clearValue();
      if (reporterSelectRef.current) reporterSelectRef.current.clearValue();
      if (clientSelectRef.current) clientSelectRef.current.clearValue();
      if (typeSelectRef.current) typeSelectRef.current.clearValue();
      if (prioritySelectRef.current) prioritySelectRef.current.clearValue();
      if (productSelectRef.current) productSelectRef.current.clearValue();
      if (tsoSelectRef.current) tsoSelectRef.current.clearValue();

      // Fetch all tickets for today to reset the data
      const currentDate = new Date().toISOString().split("T")[0];
      await fetchIssues(currentDate, currentDate);

      showPopupMessage("All filters cleared");
    } catch (error) {
      console.error("Error clearing filters:", error);
      showPopupMessage("Error clearing filters");
    }
  };

  const handleTsoChange = async (e, issue) => {
    const tsoId = e.target.value;
    if (tsoId) {
      try {
        const token = Cookies.get("authToken");
        if (!token) {
          throw new Error("Authentication token not found.");
        }

        const response = await fetch(
          `${config.API_URL}/tso-online/assign-ticket-to-tso`,
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              tso_id: tsoId,
              ticket_id: issue.ticket_id,
              customer_id: issue.customer_id,
              issue: issue.issue,
              scheduled_date: issue.ticket_created_date,
            }),
          }
        );

        if (response.ok) {
          const statusResponse = await fetch(
            `${config.API_URL}/tso-online/change-ticket-status`,
            {
              method: "PUT",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                ticket_id: issue.ticket_id,
                ticket_status: "in_progress",
              }),
            }
          );

          if (statusResponse.ok) {
            showPopupMessage(
              "Ticket assigned successfully and status updated to in progress!"
            );
            // Refresh the data immediately to show updated status and TSO
            await refetchCurrentData();
          } else {
            showPopupMessage("Ticket assigned but failed to update status");
            // Still refresh to show the assignment
            await refetchCurrentData();
          }
        } else {
          showPopupMessage("Failed to assign ticket");
        }
      } catch (error) {
        console.error("Error assigning ticket:", error);
        showPopupMessage("An error occurred while assigning the ticket");
      }
    }
  };

  const handleCheck = (issue) => {
    if (!issue) {
      showPopupMessage("Issue is null.");
      return;
    }

    if (!issue.assigned_tso_id) {
      showPopupMessage("Please select a TSO before proceeding.");
      return;
    }

    if (issue.ticket_status === "pending") {
      showPopupMessage("Ticket status cannot be pending.");
      return;
    }

    if (!issue.assigned_tso_id || !currentUser.id) {
      showPopupMessage("Assigned TSO ID or Current User ID is missing.");
      return;
    }

    if (issue.assigned_tso_id.toString() !== currentUser.id.toString()) {
      showPopupMessage(
        "You should be assigned to this issue to proceed with this ticket."
      );
      return;
    }

    setSelectedIssue({
      ...issue,
      issueType: "",
      comment: "",
      successfullyCompleted: false,
      needsFollowUp: false,
      followUpNotes: "",
    });
    setShowPopup(true);
    setFormErrors({});
  };

  const handleViewTicket = (issue) => {
    setSelectedTicket(issue);
    setShowTicketPopup(true);
  };

  const handleTicketUpdate = () => {
    fetchData();
  };

  const openUpdateModal = (ticket) => {
    setTicketToUpdate(ticket);
    setContactPhone(ticket.contact_person_phone_number || "");
    setFilesToUpload(null);
    setShowUpdateModal(true);
  };

  const handleFileChange = (e) => {
    setFilesToUpload(e.target.files);
  };

  const updateTicketInfo = async () => {
    if (!contactPhone && (!filesToUpload || filesToUpload.length === 0)) {
      showPopupMessage(
        "Please provide either a phone number or files to update"
      );
      return;
    }

    setUploading(true);
    try {
      const token = Cookies.get("authToken");
      if (!token) {
        throw new Error("Authentication token not found.");
      }

      const formData = new FormData();

      if (contactPhone) {
        formData.append("contact_person_phone_number", contactPhone);
      }

      if (filesToUpload) {
        for (let i = 0; i < filesToUpload.length; i++) {
          formData.append("files", filesToUpload[i]);
        }
      }

      const response = await fetch(
        `${config.API_URL}/tso-online/update-files-and-contact/${ticketToUpdate.ticket_id}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to update ticket");
      }

      showPopupMessage("Ticket updated successfully!");
      setShowUpdateModal(false);
      // Socket will trigger refresh automatically
    } catch (error) {
      console.error("Error updating ticket:", error);
      showPopupMessage(
        error.message || "Error updating ticket. Please try again."
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Dashboard</h1>
        <div className="header-controls">
          <div className="socket-status-indicator">
            {socketConnected ? (
              <span className="socket-connected">Live Updates</span>
            ) : (
              <span className="socket-disconnected">Disconnected</span>
            )}
          </div>
          {lastUpdated && (
            <span className="last-updated">
              Last updated: {formatDateTime(lastUpdated, true)}
            </span>
          )}
          <button
            onClick={handleRefresh}
            className="refresh-button"
            disabled={isRefreshing}
          >
            <FaSync className={isRefreshing ? "spin-icon" : ""} />
          </button>
        </div>
      </div>

      {alertMessage && (
        <PopupMessage message={alertMessage} onClose={closePopupMessage} />
      )}

      <div className="modern-filter-section">
        <div className="filter-header">
          <h3 className="filter-title">Quick Filters</h3>
          <div className="quick-buttons">
            <button
              className={`quick-filter-btn ${
                activeQuickFilter === "today" ? "active" : ""
              }`}
              onClick={() => filterIssues("today")}
            >
              📅 Today
            </button>
            <button
              className={`quick-filter-btn ${
                activeQuickFilter === "all" ? "active" : ""
              }`}
              onClick={() => filterIssues("all")}
            >
              📋 All
            </button>
            <button
              className={`quick-filter-btn ${
                activeQuickFilter === "myTasks" ? "active" : ""
              }`}
              onClick={() => filterIssues("myTasks")}
            >
              ✓ My Tasks
            </button>
            <button
              className="quick-filter-btn clear-filters-btn"
              onClick={clearAllFilters}
              title="Clear all active filters"
            >
              ✕ Clear All
            </button>
          </div>
        </div>

        <div className="filter-controls">
          <div className="search-bar-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search tickets by issue..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="modern-search-input"
            />
          </div>

          <div className="advanced-filters">
            {statuses && (
              <div className="filter-select-wrapper">
                <Select
                  ref={statusSelectRef}
                  options={statuses.map((status) => ({
                    value: status,
                    label: status
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (c) => c.toUpperCase()),
                  }))}
                  placeholder="Status"
                  onChange={(option) => fetchIssuesByStatus(option?.value)}
                  isClearable
                  className="modern-filter-select"
                />
              </div>
            )}

            <div className="filter-select-wrapper">
              <Select
                ref={reporterSelectRef}
                options={filterOptions.reporters.map((reporter) => ({
                  value: reporter,
                  label: reporter,
                }))}
                placeholder="Reporter"
                onChange={(option) => setReporterFilter(option?.value || "")}
                isClearable
                className="modern-filter-select"
              />
            </div>

            <div className="filter-select-wrapper">
              <Select
                ref={clientSelectRef}
                options={filterOptions.clients.map((client) => ({
                  value: client,
                  label: client,
                }))}
                placeholder="Client"
                onChange={(option) => setClientFilter(option?.value || "")}
                isClearable
                className="modern-filter-select"
              />
            </div>

            <div className="filter-select-wrapper">
              <Select
                ref={typeSelectRef}
                options={filterOptions.types.map((type) => ({
                  value: type,
                  label: type,
                }))}
                placeholder="Type"
                onChange={(option) => setTypeFilter(option?.value || "")}
                isClearable
                className="modern-filter-select"
              />
            </div>

            <div className="filter-select-wrapper">
              <Select
                ref={prioritySelectRef}
                options={filterOptions.priorities.map((priority) => ({
                  value: priority,
                  label: priority,
                }))}
                placeholder="Priority"
                onChange={(option) => setPriorityFilter(option?.value || "")}
                isClearable
                className="modern-filter-select"
              />
            </div>

            <div className="filter-select-wrapper">
              <Select
                ref={productSelectRef}
                options={filterOptions.products.map((product) => ({
                  value: product,
                  label: product,
                }))}
                placeholder="Product"
                onChange={(option) => setProductFilter(option?.value || "")}
                isClearable
                className="modern-filter-select"
              />
            </div>

            <div className="filter-select-wrapper">
              <Select
                ref={tsoSelectRef}
                options={filterOptions.tsos.map((tso) => ({
                  value: tso,
                  label: tso,
                }))}
                placeholder="TSO"
                onChange={(option) => setTsoFilter(option?.value || "")}
                isClearable
                className="modern-filter-select"
              />
            </div>

            <div className="date-picker-wrapper">
              <div className="date-filter-container">
                {/* Toggle buttons inline */}
                <div className="date-mode-selector">
                  <button
                    className={`date-mode-option ${
                      dateFilterMode === "single" ? "active" : ""
                    }`}
                    onClick={() => {
                      setDateFilterMode("single");
                      setStartDate(null);
                      setEndDate(null);
                    }}
                  >
                    Single
                  </button>
                  <button
                    className={`date-mode-option ${
                      dateFilterMode === "range" ? "active" : ""
                    }`}
                    onClick={() => {
                      setDateFilterMode("range");
                      setSelectedDate(null);
                    }}
                  >
                    Range
                  </button>
                </div>

                {/* Date pickers with more space */}
                {dateFilterMode === "single" ? (
                  <DatePicker
                    selected={selectedDate}
                    onChange={(date) => setSelectedDate(date)}
                    dateFormat="yyyy-MM-dd"
                    placeholderText="Select Date"
                    className="modern-date-picker"
                    isClearable
                  />
                ) : (
                  <div className="date-range-inputs">
                    <DatePicker
                      selected={startDate}
                      onChange={(date) => setStartDate(date)}
                      dateFormat="yyyy-MM-dd"
                      placeholderText="Start Date"
                      className="modern-date-picker"
                      isClearable
                    />
                    <span className="date-separator">→</span>
                    <DatePicker
                      selected={endDate}
                      onChange={(date) => setEndDate(date)}
                      dateFormat="yyyy-MM-dd"
                      placeholderText="End Date"
                      className="modern-date-picker"
                      minDate={startDate}
                      isClearable
                    />
                  </div>
                )}
              </div>{" "}
            </div>

            <div className="filter-count-display">
              {" "}
              <span className="count-badge">
                {uniqueFilteredIssues.length}
              </span>{" "}
              ticket{uniqueFilteredIssues.length !== 1 ? "s" : ""}
            </div>
          </div>
        </div>
      </div>

      {error && <div className="error-message">Error: {error}</div>}

      {uniqueFilteredIssues.length > 0 ? (
        <>
          <table
            className={`dashboard-table ${
              selectedStatus === "completed" ? "completed-status" : ""
            }`}
          >
            <thead>
              <tr>
                <th>ID</th>
                <th>Type</th>
                <th>Priority</th>
                <th>Product</th>
                <th>Issue</th>
                <th>Client</th>
                <th className="contact-phone-col">Contact Phone</th>
                <th className="reported-by-col">Reported By</th>
                <th>Images</th>
                <th>Date,Time</th>
                <th>Status</th>
                <th className="tso-col">TSO</th>
                {selectedStatus !== "completed" && <th>Update Info</th>}
                {selectedStatus !== "completed" && <th>Action</th>}
                <th>View Ticket</th>
              </tr>
            </thead>
            <tbody>
              {paginatedIssues.map((issue) => (
                <tr key={issue.ticket_id}>
                  <td>{issue.ticket_no}</td>
                  <td>
                    {issue.ticket_type ? (
                      <span className="type-badge">{issue.ticket_type}</span>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td>
                    {issue.ticket_priority ? (
                      <span
                        className={`priority-badge priority-${issue.ticket_priority.toLowerCase()}`}
                      >
                        {issue.ticket_priority}
                      </span>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td>
                    {issue.product ? (
                      <span className="product-badge">{issue.product}</span>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td>{issue.issue}</td>
                  <td>{issue.customer_name}</td>
                  <td className="contact-phone-col">
                    {issue.contact_person_phone_number || "-"}
                  </td>
                  <td className="reported-by-col">
                    {issue.created_by_user_type === "client"
                      ? `${issue.customer_name}`
                      : `${issue.created_by || "-"}`}
                  </td>
                  <td>
                    {issue.filename ? (
                      <>
                        {getFileList(issue.filename).map((_, index) => (
                          <FiImage
                            key={index}
                            className="resource-list"
                            onClick={() => {
                              openResource(
                                issue,
                                getFileList(issue.filename)[index]
                              );
                            }}
                            title={`View Resource ${index + 1}`}
                            size={15}
                            color="#1C3663"
                          />
                        ))}
                      </>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td>
                    <div className="date-time-display">
                      <input
                        value={formatDateTime(issue.ticket_created_date)}
                        readOnly
                        style={{
                          border: "none",
                          backgroundColor: "#cce5ff",
                          color: "black",
                          fontFamily: "inherit",
                          fontSize: "inherit",
                          fontStyle: "normal",
                          width: "70%",
                          overflowWrap: "break-word",
                          cursor: "default",
                          borderRadius: "100px",
                        }}
                      />
                    </div>
                  </td>
                  <td style={getStatusColor(issue.ticket_status)}>
                    {formatStatusText(issue.ticket_status)}
                  </td>
                  <td className="tso-col">
                    {issue.ticket_status === "completed" ? (
                      <div className="completed-by-display">
                        <input
                          value={`Completed by ${
                            issue.assigned_tso_name || "Unknown"
                          }`}
                          readOnly
                          style={{
                            border: "none",
                            backgroundColor: "#D5FBC8",
                            color: "black",
                            fontFamily: "inherit",
                            fontSize: "inherit",
                            fontStyle: "normal",
                            width: "90%",
                            overflowWrap: "break-word",
                            cursor: "default",
                          }}
                        />
                      </div>
                    ) : (
                      <select
                        value={issue.assigned_tso_id || ""}
                        onChange={(e) => handleTsoChange(e, issue)}
                        disabled={
                          !["pending", "in_progress"].includes(
                            issue.ticket_status
                          ) || loading
                        }
                      >
                        {!issue.assigned_tso_id && (
                          <option value="" disabled>
                            {loading ? "Loading TSOs..." : "Select TSO"}
                          </option>
                        )}

                        {issue.assigned_tso_id && (
                          <option value={issue.assigned_tso_id} disabled>
                            Assigned to {issue.assigned_tso_name || "Unknown"}
                          </option>
                        )}

                        {currentUser.id && (
                          <option value={currentUser.id}>
                            Assign Me ({currentUser.calling_name})
                          </option>
                        )}

                        {assignedTSOs[issue.customer_id] &&
                          assignedTSOs[issue.customer_id].map((tso) => (
                            <option key={tso.id} value={tso.id}>
                              {tso.calling_name ||
                                tso.full_name ||
                                `TSO ${tso.id}`}
                            </option>
                          ))}
                      </select>
                    )}
                  </td>
                  {issue.ticket_status !== "completed" && (
                    <td>
                      <FiEdit
                        className="view-icon"
                        size={15}
                        color="#1C3663"
                        onClick={() => openUpdateModal(issue)}
                        title="Update ticket info"
                      />
                    </td>
                  )}
                  {issue.ticket_status !== "completed" && (
                    <td>
                      <FaCheck
                        className="action-icon"
                        size={15}
                        color="green"
                        onClick={() => handleCheck(issue)}
                        title="Click to update"
                      />
                    </td>
                  )}
                  <td>
                    <FaTicketAlt
                      className="view-icon"
                      size={15}
                      color="#1C3663"
                      onClick={() => handleViewTicket(issue)}
                      title="View Ticket"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {totalPages > 1 && (
            <div className="pagination-controls">
              <span className="pagination-info">
                Page {currentPage} of {totalPages} | Showing{" "}
                {paginatedIssues.length} of {sortedIssues.length} tickets
              </span>
              <div className="pagination-buttons">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="pagination-btn"
                >
                  ← Previous
                </button>
                <button
                  onClick={() =>
                    setCurrentPage(Math.min(totalPages, currentPage + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="pagination-btn"
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <p>No issues found.</p>
      )}

      {showPopup && selectedIssue && (
        <div className="popup-overlay">
          <div className="popup-content">
            <h3>Confirm Update</h3>
            <div className="form-group">
              <label>
                <strong>Issue:</strong> {selectedIssue.issue}
              </label>
            </div>

            <div className="form-group">
              <label>
                Issue Type: <span className="required-field">*</span>
              </label>
              <select
                value={selectedIssue.issueType || ""}
                onChange={(e) =>
                  setSelectedIssue({
                    ...selectedIssue,
                    issueType: e.target.value,
                  })
                }
                className={formErrors.issueType ? "error-field" : ""}
              >
                <option value="">Select Issue Type</option>
                {issueTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
              {formErrors.issueType && (
                <span className="error-message">{formErrors.issueType}</span>
              )}
            </div>

            <div className="form-group">
              <label>
                Comment: <span className="required-field">*</span>
              </label>
              <textarea
                placeholder="Add a comment (minimum 3 characters)"
                value={selectedIssue.comment || ""}
                onChange={(e) =>
                  setSelectedIssue({
                    ...selectedIssue,
                    comment: e.target.value,
                  })
                }
                className={formErrors.comment ? "error-field" : ""}
              />
              {formErrors.comment && (
                <span className="error-message">{formErrors.comment}</span>
              )}
            </div>

            <div className="form-group-checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={selectedIssue.successfullyCompleted || false}
                  onChange={(e) =>
                    setSelectedIssue({
                      ...selectedIssue,
                      successfullyCompleted: e.target.checked,
                    })
                  }
                  className={
                    formErrors.successfullyCompleted ? "error-field" : ""
                  }
                />
                Successfully Completed <span className="required-field">*</span>
              </label>
              {formErrors.successfullyCompleted && (
                <span className="error-message">
                  {formErrors.successfullyCompleted}
                </span>
              )}
            </div>

            <div className="form-group-checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={selectedIssue.needsFollowUp || false}
                  onChange={(e) =>
                    setSelectedIssue({
                      ...selectedIssue,
                      needsFollowUp: e.target.checked,
                    })
                  }
                />
                Needs Follow-up Action
              </label>
            </div>

            {selectedIssue.needsFollowUp && (
              <div className="form-group">
                <label>Follow-up Notes:</label>
                <textarea
                  placeholder="Add follow-up notes"
                  value={selectedIssue.followUpNotes || ""}
                  onChange={(e) =>
                    setSelectedIssue({
                      ...selectedIssue,
                      followUpNotes: e.target.value,
                    })
                  }
                />
              </div>
            )}

            <div className="button-group">
              <button
                className="confirm"
                onClick={async () => {
                  if (!validateForm()) return;

                  setShowPopup(false);
                  const fullComment = [
                    selectedIssue.comment,
                    selectedIssue.issueType &&
                      `Type: ${selectedIssue.issueType}`,
                    selectedIssue.followUpNotes &&
                      `Follow-up: ${selectedIssue.followUpNotes}`,
                  ]
                    .filter(Boolean)
                    .join(" | ");

                  handleAssignToMe(
                    currentUser.id,
                    selectedIssue.ticket_id,
                    selectedIssue.customer_id,
                    selectedIssue.issue,
                    selectedIssue.ticket_created_date,
                    fullComment
                  );
                  setSelectedIssue(null);
                }}
              >
                Confirm
              </button>
              <button onClick={() => setShowPopup(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {imageSrc && (
        <div className="modal">
          <div className="modalContent">
            <span className="close" onClick={closeImageModal}>
              &times;
            </span>
            <h3>File Details</h3>
            <p>Name: {selectedFile.name}</p>
            <p>Type: {selectedFile.type}</p>
            <img src={imageSrc} alt="Selected" style={{ maxWidth: "100%" }} />
          </div>
        </div>
      )}

      {showTicketPopup && selectedTicket && currentUser && (
        <TicketPopup
          ticket={selectedTicket}
          isOpen={showTicketPopup}
          onClose={() => setShowTicketPopup(false)}
          currentUser={currentUser}
          onTicketUpdate={handleTicketUpdate}
          showPopupMessage={showPopupMessage}
          socket={socketRef.current}
        />
      )}

      {showUpdateModal && ticketToUpdate && (
        <div className="modal">
          <div className="modalContent">
            <span className="close" onClick={() => setShowUpdateModal(false)}>
              &times;
            </span>
            <h3>Update Ticket Information</h3>
            <div className="form-group">
              <label>Contact Phone Number:</label>
              <input
                type="tel"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                placeholder="Enter phone number"
              />
            </div>
            <div className="form-group">
              <label>Upload Files:</label>
              <input type="file" onChange={handleFileChange} multiple />
              <small>You can upload multiple files</small>
            </div>
            <div className="button-group">
              <button
                onClick={updateTicketInfo}
                disabled={uploading}
                className="confirm"
              >
                {uploading ? "Updating..." : "Update"}
              </button>
              <button onClick={() => setShowUpdateModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
